'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { useLabsSurface } from '../labs-surface';
import { useLabsStore, usePack } from '../pack-context';
import { MODULE_COUNT, type Step } from '../steps';
import { useLabsTrialBar } from '../use-labs-trial-bar';
import { ProgressBar } from './progress-bar';

/**
 * Shared Labs chrome — the progress indicator. The Back affordance is NOT here: it's paired with
 * each screen's primary "Continue" via <StepNav> in the screen body, so the two step controls sit
 * together. Canvas #fdf8f8, centered 694px content column.
 *
 * The flow does NOT render its own top bar: it lives inside the platform shell ((platform)/layout.tsx)
 * which already supplies the single global app-bar (Home · extern wordmark · account/menu) on every
 * screen. The platform app-bar is the sole header — a second, near-identical banner here would
 * duplicate it (the standalone prototype had no platform chrome; the sprint does).
 *
 * CHROME:
 *   Sub-nav row — a slim row directly below the platform app-bar carrying the PROGRESS pill + live %
 *   (right-aligned). Hidden on welcome + the recommendations results page.
 *
 * PROGRESS MODEL ("the bar moves ONLY on screens where the user performs a task"):
 *   The four MODULES are weighted EVENLY — each is worth 25% of the bar, regardless of how many
 *   tasks it holds. Within a module the 25% fills proportionally across that module's action tasks:
 *     • M1 (25%) — the swipe deck: fills 0→25% across the pack's cards (one task per card)
 *     • M2 (25%) — the mindset quiz: fills 25→50% across the pack's questions (one per question)
 *     • M3 (25%) — the "read the numbers" screen: its single task carries the whole 25%
 *     • M4 (25%) — splits its 25% across two tasks: pick a subject (GATED: fills 75→87.5% WHEN a
 *       subject is chosen, not on entry), then analyze — the bar HOLDS at 87.5% through the analysis
 *       and only reaches 100% when the user SUBMITS ("See my analysis"), so it never shows 100% before
 *       the submission is finished.
 *   Every PASSIVE screen — welcome, every module intro, the M3 reading primer, every results screen,
 *   and every INTERMEDIATE `*-complete` celebration — HOLDS the bar flat at the last module boundary;
 *   it NEVER moves on a screen with nothing to do. The SOLE exception is the terminal m4-complete,
 *   which reaches 100% as the payoff of the analysis submission. So the bar reads:
 *     m1-intro 0% → (swipe fills 0→25%) → holds 25% through m1-results/complete + m2-intro →
 *     (quiz fills 25→50%) → holds 50% through m2-results/complete + m3-intro/reading →
 *     m3-activity 75% → holds 75% through m3-complete + m4-intro + m4-selection (75%) → 88% on pick →
 *     m4-activity holds 88% → m4-complete 100% (on submit). welcome + recommendations show no bar.
 *
 * The M1/M2 task COUNTS come from the active pack (`m1.cards.length` / `m2.questions.length`), not
 * from a constant: a vertical may ship a different-sized deck or quiz, and each module still owns
 * exactly 25% of the bar either way. The step VOCABULARY, by contrast, is engine-owned — every
 * vertical runs the same 18 steps (see `../steps.ts`), so the switch below is exhaustive for all of
 * them.
 *
 * `tasksCompleted` is the INTRA-MODULE task count the GATED action screens pass up: M1 the number of
 * cards resolved, M2 the number of questions answered, m4-selection 1 once a subject is picked (else
 * 0). Every other screen ignores it (it credits its slice by step) — omit it elsewhere.
 */

/** Steps with no progress bar: the intro + the terminal results page. */
function progressHiddenByDefault(step: string): boolean {
  return step === 'welcome' || step === 'recommendations';
}

/** Fraction (0..1) of a module completed, from `n` of `max` intra-module tasks done. */
const moduleFrac = (n: number, max: number): number => Math.min(Math.max(n, 0), max) / max;

/** The single task that gates m4-selection: one subject picked. */
const M4_PICK_TASKS = 1;
/** M4's 25% splits evenly into pick + analyze, so each is worth half a module. */
const M4_PICK_WEIGHT = 0.5;

/**
 * Module progress at `step`, given `intra` — the count of sub-tasks done WITHIN the current GATED
 * action screen (cards resolved on m1-activity / questions answered on m2-activity / 1-if-picked on
 * m4-selection; ignored elsewhere) and the pack's `m1Tasks` / `m2Tasks` deck+quiz sizes. Returns
 * { done, frac }: `done` = modules FULLY complete (0..4), `frac` = fraction through the in-progress
 * module. Fed to <ProgressBar current={done} total={4} fraction={frac}> → pct = (done + frac) / 4 ×
 * 100. The gated action screens fill their module per task; M3 credits its whole 25% by step; the M4
 * ANALYZE screen holds at 87.5% and defers its last 12.5% to m4-complete (100% only on submit). Net:
 * the bar moves only as the user actually completes work, and holds flat on every passive screen
 * except the terminal m4-complete (the submit payoff).
 */
function moduleProgressAt(step: Step, intra: number, m1Tasks: number, m2Tasks: number): {
  done: number;
  frac: number;
} {
  switch (step) {
    case 'welcome':
    case 'm1-intro':
      return {
        done: 0,
        frac: 0
      };
    // 0%
    case 'm1-activity':
      return {
        done: 0,
        frac: moduleFrac(intra, m1Tasks)
      };
    // 0→25% — per swipe / skip
    case 'm1-results':
    case 'm1-complete':
    case 'm2-intro':
      return {
        done: 1,
        frac: 0
      };
    // 25% — holds
    case 'm2-activity':
      return {
        done: 1,
        frac: moduleFrac(intra, m2Tasks)
      };
    // 25→50% — per answer
    case 'm2-results':
    case 'm2-complete':
    case 'm3-intro':
    case 'm3-reading':
      return {
        done: 2,
        frac: 0
      };
    // 50% — holds (m3-reading is the passive primer)
    case 'm3-activity':
    case 'm3-complete':
    case 'm4-intro':
      return {
        done: 3,
        frac: 0
      };
    // 75% — M3's whole 25% credited on its activity screen, then holds
    case 'm4-selection':
      // M4's 25% splits into pick (1st 12.5%) + analyze (2nd 12.5%). The pick is GATED (can't advance
      // without choosing a subject), so it fills 75→87.5% WHEN a subject is picked — not on entry.
      // `intra` = 1 once a subject is selected, else 0.
      return {
        done: 3,
        frac: M4_PICK_WEIGHT * moduleFrac(intra, M4_PICK_TASKS)
      };
    // 75% → 87.5%
    case 'm4-activity':
      // The ANALYSIS / submission screen. The pick is done (→ 87.5%); the analysis is pending, so the
      // bar HOLDS at 87.5% here and only reaches 100% when the user SUBMITS ("See my analysis"),
      // landing on m4-complete. It never shows 100% before the submission is finished.
      return {
        done: 3,
        frac: M4_PICK_WEIGHT
      };
    // 87.5% — holds through the analysis
    case 'm4-complete':
    case 'recommendations':
      return {
        done: MODULE_COUNT,
        frac: 0
      };
    // 100% — the analysis submission is finished
    default:
      {
        const _exhaustive: never = step;
        return _exhaustive;
      }
  }
}
export function StepShell(t0) {
  const $ = _c(25);
  const {
    children,
    showProgress,
    tasksCompleted,
    contentClassName,
    contentAlign: t1
  } = t0;
  const contentAlign = t1 === undefined ? "center" : t1;
  const {
    m1,
    m2
  } = usePack();
  const step = useLabsStore(_temp);
  const {
    slot: trialBarVisible
  } = useLabsTrialBar();
  const {
    moduleCount
  } = useLabsSurface();
  let t2;
  if ($[0] !== showProgress || $[1] !== step) {
    t2 = showProgress ?? !progressHiddenByDefault(step);
    $[0] = showProgress;
    $[1] = step;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const progressVisible = t2;
  const t3 = tasksCompleted ?? 0;
  let t4;
  if ($[3] !== m1.cards.length || $[4] !== m2.questions.length || $[5] !== step || $[6] !== t3) {
    t4 = moduleProgressAt(step, t3, m1.cards.length, m2.questions.length);
    $[3] = m1.cards.length;
    $[4] = m2.questions.length;
    $[5] = step;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const raw = t4;
  const isTerminal = step === "m4-complete" || step === "recommendations";
  const done = isTerminal ? raw.done : Math.min(raw.done, moduleCount - 1);
  const frac = raw.frac;
  const t5 = trialBarVisible ? "flex-1" : "min-h-[calc(100dvh-theme(spacing.16))]";
  let t6;
  if ($[8] !== t5) {
    t6 = cn("flex flex-col overflow-x-clip bg-fs-canvas", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== done || $[11] !== frac || $[12] !== moduleCount || $[13] !== progressVisible) {
    t7 = progressVisible ? <div className="flex w-full items-center justify-end px-4 py-2.5"><ProgressBar current={done} fraction={frac} total={moduleCount} /></div> : null;
    $[10] = done;
    $[11] = frac;
    $[12] = moduleCount;
    $[13] = progressVisible;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = contentAlign === "center" && "my-auto";
  let t9;
  if ($[15] !== contentClassName || $[16] !== t8) {
    t9 = cn("w-full max-w-[694px]", t8, contentClassName);
    $[15] = contentClassName;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== children || $[19] !== t9) {
    t10 = <div className="flex flex-1 flex-col items-center px-4 pt-3 pb-4 sm:pt-4 sm:pb-12"><div className={t9}>{children}</div></div>;
    $[18] = children;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6 || $[23] !== t7) {
    t11 = <div className={t6}>{t7}{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function _temp(s) {
  return s.step;
}